import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Megaphone,
  Plus,
  Search,
  Calendar,
  User,
  ArrowLeft,
  Edit,
  Trash2,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Announcements = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // States
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  
  // Form states
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchMyProfile();
      fetchAnnouncements();
    }
  }, [user]);

  const fetchMyProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
      if (response.ok) {
        const profile = await response.json();
        setMyProfile(profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/announcements`);
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: t('error'),
          description: 'Image size must be less than 5MB',
          variant: "destructive"
        });
        return;
      }
      setNewImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      toast({
        title: t('error'),
        description: 'Please fill in all fields',
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    let imageUrl = null;

    try {
      // Upload image first if selected
      if (newImage) {
        const formData = new FormData();
        formData.append('file', newImage);
        
        const uploadResponse = await fetch(`${BACKEND_URL}/api/announcements/upload-image`, {
          method: 'POST',
          body: formData
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData.image_url;
        } else {
          throw new Error('Failed to upload image');
        }
      }

      // Create announcement
      const response = await fetch(`${BACKEND_URL}/api/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle,
          content: newContent,
          author_id: myProfile.id,
          author_name: myProfile.name,
          image_url: imageUrl
        })
      });

      if (response.ok) {
        toast({
          title: t('success'),
          description: 'Announcement created successfully'
        });
        setNewTitle('');
        setNewContent('');
        setNewImage(null);
        setImagePreview(null);
        setCreateDialogOpen(false);
        fetchAnnouncements();
      } else {
        throw new Error('Failed to create announcement');
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
      toast({
        title: t('error'),
        description: error.message || 'Failed to create announcement',
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEditImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: t('error'),
          description: 'Image size must be less than 5MB',
          variant: "destructive"
        });
        return;
      }
      setEditImage(file);
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  const handleEditAnnouncement = async () => {
    if (!editingAnnouncement) return;

    setUploading(true);
    let imageUrl = editingAnnouncement.image_url; // Keep existing image by default

    try {
      // Upload new image if selected
      if (editImage) {
        const formData = new FormData();
        formData.append('file', editImage);
        
        const uploadResponse = await fetch(`${BACKEND_URL}/api/announcements/upload-image`, {
          method: 'POST',
          body: formData
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData.image_url;
        } else {
          throw new Error('Failed to upload image');
        }
      }

      // Update announcement
      const response = await fetch(`${BACKEND_URL}/api/announcements/${editingAnnouncement.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingAnnouncement.title,
          content: editingAnnouncement.content,
          image_url: imageUrl
        })
      });

      if (response.ok) {
        toast({
          title: t('success'),
          description: 'Announcement updated successfully'
        });
        setEditDialogOpen(false);
        setEditingAnnouncement(null);
        setEditImage(null);
        setEditImagePreview(null);
        fetchAnnouncements();
      } else {
        throw new Error('Failed to update announcement');
      }
    } catch (error) {
      console.error('Error updating announcement:', error);
      toast({
        title: t('error'),
        description: error.message || 'Failed to update announcement',
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAnnouncement = async () => {
    if (!deletingAnnouncement) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/announcements/${deletingAnnouncement.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('success'),
          description: 'Announcement deleted successfully'
        });
        setDeleteDialogOpen(false);
        setDeletingAnnouncement(null);
        fetchAnnouncements();
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast({
        title: t('error'),
        description: 'Failed to delete announcement',
        variant: "destructive"
      });
    }
  };

  const filteredAnnouncements = announcements.filter(announcement => {
    const query = searchQuery.toLowerCase();
    return (
      announcement.title.toLowerCase().includes(query) ||
      announcement.content.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Megaphone className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t('authenticationRequired')}</h2>
            <p className="text-gray-600 mb-4">{t('signInToView')}</p>
            <Button onClick={() => navigate('/')} className="bg-cyan-600 hover:bg-cyan-700">
              {t('signIn')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('back')}
              </Button>
              <div className="flex items-center gap-3">
                <Megaphone className="h-8 w-8 text-cyan-600" />
                <h1 className="text-2xl font-bold text-gray-900">{t('announcements')}</h1>
              </div>
            </div>
            
            {myProfile && (
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="gap-2 bg-cyan-600 hover:bg-cyan-700"
              >
                <Plus className="h-4 w-4" />
                {t('createAnnouncement')}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('searchAnnouncements')}
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Announcements List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('loading')}</p>
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Megaphone className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchQuery ? t('noResults') : t('noAnnouncements')}
              </h3>
              <p className="text-gray-600">
                {searchQuery ? t('tryDifferentSearch') : t('beFirstToPost')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredAnnouncements.map((announcement) => (
              <Card key={announcement.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{announcement.title}</CardTitle>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {announcement.author_name}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(announcement.created_at)}
                        </div>
                      </div>
                    </div>
                    
                    {myProfile && myProfile.id === announcement.author_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditingAnnouncement(announcement);
                              setEditDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            {t('edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setDeletingAnnouncement(announcement);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {announcement.image_url && (
                    <div className="mb-4 bg-gray-50 rounded-lg p-2 flex items-center justify-center">
                      <img 
                        src={`${BACKEND_URL}${announcement.image_url}`}
                        alt={announcement.title}
                        className="rounded-lg max-h-64 w-auto object-contain"
                      />
                    </div>
                  )}
                  <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Announcement Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('createAnnouncement')}</DialogTitle>
            <DialogDescription>
              {t('createAnnouncementDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('title')}
              </label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t('enterTitle')}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('content')}
              </label>
              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder={t('enterContent')}
                rows={6}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('image')} ({t('optional')})
              </label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="cursor-pointer"
              />
              {imagePreview && (
                <div className="mt-3 relative">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="max-h-48 rounded-lg object-cover"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setNewImage(null);
                      setImagePreview(null);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('maxImageSize')} 5MB
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={uploading}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleCreateAnnouncement} 
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={uploading}
            >
              {uploading ? t('uploading') : t('createAnnouncement')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Announcement Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('editAnnouncement')}</DialogTitle>
          </DialogHeader>
          
          {editingAnnouncement && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('title')}
                </label>
                <Input
                  value={editingAnnouncement.title}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, title: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('content')}
                </label>
                <Textarea
                  value={editingAnnouncement.content}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, content: e.target.value})}
                  rows={6}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('image')} ({t('optional')})
                </label>
                
                {/* Current Image Preview */}
                {editingAnnouncement.image_url && !editImagePreview && (
                  <div className="mb-3 relative bg-gray-50 rounded-lg p-2 flex items-center justify-center">
                    <img 
                      src={`${BACKEND_URL}${editingAnnouncement.image_url}`}
                      alt="Current" 
                      className="max-h-32 rounded-lg object-contain"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setEditingAnnouncement({...editingAnnouncement, image_url: null});
                      }}
                    >
                      Remove Current
                    </Button>
                  </div>
                )}

                {/* New Image Upload */}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleEditImageSelect}
                  className="cursor-pointer"
                />
                
                {/* New Image Preview */}
                {editImagePreview && (
                  <div className="mt-3 relative">
                    <img 
                      src={editImagePreview} 
                      alt="Preview" 
                      className="max-h-48 rounded-lg object-cover"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setEditImage(null);
                        setEditImagePreview(null);
                      }}
                    >
                      Remove New
                    </Button>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {t('maxImageSize')} 5MB
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={uploading}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleEditAnnouncement} 
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={uploading}
            >
              {uploading ? t('uploading') : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deleteAnnouncement')}</DialogTitle>
            <DialogDescription>
              {t('confirmDeleteAnnouncement')}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleDeleteAnnouncement} variant="destructive">
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Announcements;
